/**
 * Site-wide usage tracker.
 *
 * Batches page/tab views and in-page clicks and ships them to the backend
 * `/api/usage/ingest/` endpoint. API calls themselves are tracked server-side by
 * UsageTrackingMiddleware — this module only reports the things the backend
 * can't see: which tab a user opened and which in-page controls they clicked.
 *
 * Notes:
 *  - Only authenticated users are tracked (valid JWT in localStorage).
 *  - Uses `fetch(..., { keepalive: true })` rather than navigator.sendBeacon so
 *    it can send the `Authorization` header (MIDAS keeps its JWT in
 *    localStorage, not cookies) while still surviving page unload.
 *  - All failures are swallowed: tracking must never affect the app.
 */

import { jwtDecode } from "jwt-decode";

type UsageEventType = "page_view" | "ui_event";

interface UsageEvent {
  event_type: UsageEventType;
  path: string;
  route_label?: string;
  label?: string;
  metadata?: Record<string, unknown>;
  ts: number;
  session_id: string;
}

const API_URL = process.env.REACT_APP_API_URL;
const INGEST_URL = `${API_URL}/api/usage/ingest/`;
const ACCESS = "access_token";
const SESSION_KEY = "usage_session_id";

const FLUSH_INTERVAL_MS = 8000;
const MAX_BATCH = 25;

let buffer: UsageEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// Query-param name fragments whose values must never be logged (tokens land in
// URLs on flows like /reset-password?token=... and /email-verification).
const SENSITIVE_QS_PARTS = [
  "token", "password", "passwd", "pwd", "secret", "apikey", "api_key",
  "access", "auth", "otp", "code", "sig", "credential", "key",
];

/** Redact sensitive values in a URL query string before it leaves the browser. */
export function sanitizeSearch(search: string): string {
  if (!search) return "";
  try {
    const p = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
    let changed = false;
    for (const k of Array.from(p.keys())) {
      if (SENSITIVE_QS_PARTS.some((s) => k.toLowerCase().includes(s))) {
        p.set(k, "***");
        changed = true;
      }
    }
    const out = p.toString();
    return changed ? out : search.replace(/^\?/, "");
  } catch {
    return "";
  }
}

function getSessionId(): string {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid =
        (typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return "no-session";
  }
}

function isLoggedIn(): boolean {
  const token = localStorage.getItem(ACCESS);
  if (!token) return false;
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    return !!exp && exp > Math.floor(Date.now() / 1000);
  } catch {
    return false;
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => flush(), FLUSH_INTERVAL_MS);
}

function enqueue(ev: UsageEvent): void {
  if (!API_URL || !isLoggedIn()) return; // only track authenticated users
  buffer.push(ev);
  if (buffer.length >= MAX_BATCH) {
    flush();
  } else {
    scheduleFlush();
  }
}

/** Ship the buffered events immediately. Safe to call on unload. */
export function flush(): void {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!buffer.length) return;

  const token = localStorage.getItem(ACCESS);
  if (!API_URL || !token) {
    buffer = [];
    return;
  }

  const batch = buffer;
  buffer = [];
  try {
    fetch(INGEST_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    }).catch(() => {
      /* swallow — tracking must never surface errors */
    });
  } catch {
    /* swallow */
  }
}

/** Record that the user opened a tab/page (frontend route). */
export function trackPageView(
  path: string,
  routeLabel?: string,
  metadata?: Record<string, unknown>,
): void {
  enqueue({
    event_type: "page_view",
    path,
    route_label: routeLabel,
    metadata,
    ts: Date.now(),
    session_id: getSessionId(),
  });
}

/**
 * Record an in-page interaction (sub-tab switch, button click, export, …).
 * `label` is the human name shown in the analytics dashboard.
 */
export function trackClick(
  label: string,
  metadata?: Record<string, unknown>,
): void {
  enqueue({
    event_type: "ui_event",
    path: typeof window !== "undefined" ? window.location.pathname : "",
    route_label: label,
    label,
    metadata,
    ts: Date.now(),
    session_id: getSessionId(),
  });
}

/**
 * Site-wide in-page click tracking via a single delegated listener.
 *
 * Auto-captures, with no per-component changes:
 *   - MUI sub-tab switches (elements with role="tab") — i.e. which tab within a
 *     page the user selected.
 *   - Any control explicitly tagged `data-track="Label"` (optionally with
 *     `data-track-meta='{"json":"..."}'`).
 *
 * It deliberately does NOT record every button click (too noisy); tag the
 * controls worth tracking with data-track. Idempotent — safe to call repeatedly.
 */
let clickTrackingInitialised = false;

function labelForTab(el: HTMLElement): string {
  const text = (el.textContent || "").trim().replace(/\s+/g, " ");
  return text ? `tab: ${text.slice(0, 80)}` : "tab";
}

export function initClickTracking(): void {
  if (clickTrackingInitialised || typeof document === "undefined") return;
  clickTrackingInitialised = true;

  let lastLabel = "";
  let lastTime = 0;

  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as HTMLElement | null;
      if (!target || typeof target.closest !== "function") return;

      let label = "";
      let meta: Record<string, unknown> | undefined;

      const tagged = target.closest("[data-track]") as HTMLElement | null;
      if (tagged) {
        label = tagged.getAttribute("data-track") || "";
        const rawMeta = tagged.getAttribute("data-track-meta");
        if (rawMeta) {
          try {
            meta = JSON.parse(rawMeta);
          } catch {
            meta = { raw: rawMeta.slice(0, 200) };
          }
        }
      } else {
        const tab = target.closest('[role="tab"]') as HTMLElement | null;
        if (tab) label = labelForTab(tab);
      }

      if (!label) return;

      // De-dupe rapid duplicate fires (e.g. bubbling / double render).
      const now = Date.now();
      if (label === lastLabel && now - lastTime < 400) return;
      lastLabel = label;
      lastTime = now;

      trackClick(label, meta);
    },
    true, // capture phase — see the click even if handlers stopPropagation
  );
}

// Flush pending events when the tab is hidden or unloaded so nothing is lost.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
  window.addEventListener("pagehide", () => flush());
}
